import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ReactFlow,
  Background,
  Controls,
  ConnectionMode,
  MiniMap,
  MarkerType,
  applyNodeChanges,
  type Connection,
  type Edge,
  type EdgeMouseHandler,
  type Node,
  type NodeChange,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useWorkflowCanvas } from "../hooks/useWorkflowCanvas";
import { usePipelineCanvas } from "../hooks/usePipelineCanvas";
import { useFormCanvas } from "../hooks/useFormCanvas";
import { useCanvasLinks } from "../hooks/useCanvasLinks";
import type { PipelineObjectType } from "../hooks/usePipelines";
import { buildCanvasGraph, type CanvasDirection } from "../lib/canvas-graph";
import { buildPipelineCanvasGraph } from "../lib/pipeline-canvas-graph";
import { nodeTypes } from "../components/workflow-nodes";
import CanvasSidebar, {
  type SidebarTool,
} from "../components/workflow-canvas/CanvasSidebar";
import AddWorkflowPanel from "../components/workflow-canvas/AddWorkflowPanel";
import AddPipelinePanel from "../components/workflow-canvas/AddPipelinePanel";
import AddFormPanel from "../components/workflow-canvas/AddFormPanel";
import ManualEdge from "../components/workflow-canvas/ManualEdge";
import AlignGuides from "../components/workflow-canvas/AlignGuides";
import {
  computeSnap,
  getAbsBox,
  type HGuide,
  type VGuide,
} from "../lib/canvas-snap";
import { usePortals } from "../hooks/usePortals";
import styles from "./WorkflowCanvas.module.css";

export default function WorkflowCanvas() {
  const { portalId } = useParams<{ portalId: string }>();
  const portals = usePortals();
  const portal = portals.find((p) => p.id === portalId) ?? null;

  const {
    items,
    definitions,
    loading,
    error,
    add,
    remove,
    updatePosition,
    updateDirection,
  } = useWorkflowCanvas(portalId ?? null);

  const {
    items: pipelineItems,
    definitions: pipelineDefinitions,
    loading: pipelineLoading,
    error: pipelineError,
    add: addPipeline,
    remove: removePipeline,
    updatePosition: updatePipelinePosition,
  } = usePipelineCanvas(portalId ?? null);

  const {
    items: formItems,
    definitions: formDefinitions,
    loading: formLoading,
    error: formError,
    add: addForm,
    remove: removeForm,
    updatePosition: updateFormPosition,
  } = useFormCanvas(portalId ?? null);

  const {
    links,
    error: linksError,
    add: addLink,
    updateLabel: updateLinkLabel,
    remove: removeLink,
  } = useCanvasLinks(portalId ?? null);

  const [activeTool, setActiveTool] = useState<SidebarTool>(null);

  const placedFlowIds = useMemo(
    () => new Set(items.map((i) => i.flowId)),
    [items],
  );

  const placedPipelineKeys = useMemo(
    () => new Set(pipelineItems.map((i) => `${i.objectType}:${i.pipelineId}`)),
    [pipelineItems],
  );

  const placedFormIds = useMemo(
    () => new Set(formItems.map((i) => i.formId)),
    [formItems],
  );

  const onRemove = useCallback(
    (flowId: string) => {
      void remove(flowId);
    },
    [remove],
  );

  const onChangeDirection = useCallback(
    (flowId: string, next: CanvasDirection) => {
      void updateDirection(flowId, next);
    },
    [updateDirection],
  );

  const onRemovePipeline = useCallback(
    (objectType: PipelineObjectType, pipelineId: string) => {
      void removePipeline(objectType, pipelineId);
    },
    [removePipeline],
  );

  const onRemoveForm = useCallback(
    (formId: string) => {
      void removeForm(formId);
    },
    [removeForm],
  );

  const { nodes: builtNodes, edges } = useMemo(() => {
    const wf = buildCanvasGraph(items, definitions);
    const wfNodes = wf.nodes.map((n) =>
      n.type === "workflowGroup"
        ? { ...n, data: { ...n.data, onRemove, onChangeDirection } }
        : n,
    );
    const pl = buildPipelineCanvasGraph(pipelineItems, pipelineDefinitions);
    const plNodes = pl.nodes.map((n) =>
      n.type === "pipelineGroup"
        ? { ...n, data: { ...n.data, onRemove: onRemovePipeline } }
        : n,
    );
    const formNodes: Node[] = formItems.map((f) => {
      const def = formDefinitions[f.formId];
      return {
        id: `form:${f.formId}`,
        type: "form",
        position: { x: f.positionX, y: f.positionY },
        data: {
          formId: f.formId,
          name: def?.name ?? f.formId,
          formType: def?.formType,
          onRemove: onRemoveForm,
        },
        draggable: true,
        selectable: true,
      };
    });
    const manualEdges: Edge[] = links.map((l) => ({
      id: `link:${l.id}`,
      type: "manual",
      source: l.sourceNodeId,
      target: l.targetNodeId,
      sourceHandle: l.sourceHandle ?? undefined,
      targetHandle: l.targetHandle ?? undefined,
      label: l.label ?? undefined,
      data: { manual: true, linkId: l.id, onDelete: removeLink },
      style: { stroke: "#d62a4a", strokeWidth: 2, strokeDasharray: "6 4" },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: "#d62a4a",
        width: 18,
        height: 18,
      },
    }));
    return {
      nodes: [...wfNodes, ...plNodes, ...formNodes],
      edges: [...wf.edges, ...pl.edges, ...manualEdges],
    };
  }, [
    items,
    definitions,
    pipelineItems,
    pipelineDefinitions,
    formItems,
    formDefinitions,
    links,
    removeLink,
    onRemove,
    onChangeDirection,
    onRemovePipeline,
    onRemoveForm,
  ]);

  const [nodes, setNodes] = useState<Node[]>(builtNodes);
  const nodesRef = useRef<Node[]>(builtNodes);
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    setNodes(builtNodes);
  }, [builtNodes]);

  const [vGuide, setVGuide] = useState<VGuide | null>(null);
  const [hGuide, setHGuide] = useState<HGuide | null>(null);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      let nextV: VGuide | null = null;
      let nextH: HGuide | null = null;
      let dragEnded = false;

      const adjusted = changes.map((change) => {
        if (
          change.type !== "position" ||
          !change.position ||
          change.dragging === undefined
        ) {
          return change;
        }
        if (change.dragging === false) dragEnded = true;
        const all = nodesRef.current;
        const dragged = all.find((n) => n.id === change.id);
        if (!dragged || dragged.parentId) return change;
        const measured = (
          dragged as Node & { measured?: { width?: number; height?: number } }
        ).measured;
        const w = measured?.width ?? dragged.width ?? 150;
        const h = measured?.height ?? dragged.height ?? 50;
        const proposed = {
          x: change.position.x,
          y: change.position.y,
          w,
          h,
        };
        const candidates = all
          .filter((n) => n.id !== change.id)
          .map((n) => getAbsBox(n, all));
        const snap = computeSnap(proposed, candidates);
        if (snap.vGuide) nextV = snap.vGuide;
        if (snap.hGuide) nextH = snap.hGuide;
        if (snap.dx === 0 && snap.dy === 0) return change;
        return {
          ...change,
          position: {
            x: change.position.x + snap.dx,
            y: change.position.y + snap.dy,
          },
        };
      });

      setNodes((curr) => applyNodeChanges(adjusted, curr));

      if (dragEnded) {
        setVGuide(null);
        setHGuide(null);
      } else {
        setVGuide(nextV);
        setHGuide(nextH);
      }

      for (const change of adjusted) {
        if (
          change.type !== "position" ||
          change.dragging !== false ||
          !change.position
        ) {
          continue;
        }
        if (change.id.startsWith("group:")) {
          const flowId = change.id.slice("group:".length);
          void updatePosition(flowId, change.position.x, change.position.y);
        } else if (change.id.startsWith("pipeline:")) {
          const rest = change.id.slice("pipeline:".length);
          const sep = rest.indexOf(":");
          if (sep > 0) {
            const objectType = rest.slice(0, sep) as PipelineObjectType;
            const pipelineId = rest.slice(sep + 1);
            void updatePipelinePosition(
              objectType,
              pipelineId,
              change.position.x,
              change.position.y,
            );
          }
        } else if (change.id.startsWith("form:")) {
          const formId = change.id.slice("form:".length);
          void updateFormPosition(formId, change.position.x, change.position.y);
        }
      }
    },
    [updatePosition, updatePipelinePosition, updateFormPosition],
  );

  const onConnect = useCallback(
    (c: Connection) => {
      if (!c.source || !c.target || c.source === c.target) return;
      void addLink({
        sourceNodeId: c.source,
        sourceHandle: c.sourceHandle ?? null,
        targetNodeId: c.target,
        targetHandle: c.targetHandle ?? null,
      });
    },
    [addLink],
  );

  const onEdgeClick = useCallback<EdgeMouseHandler>(
    (event, edge) => {
      const data = edge.data as
        | { manual?: boolean; linkId?: string }
        | undefined;
      if (!data?.manual || !data.linkId) return;
      if (event.altKey || event.metaKey) {
        event.stopPropagation();
        void removeLink(data.linkId);
      }
    },
    [removeLink],
  );

  const onEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      for (const e of deleted) {
        const data = e.data as
          | { manual?: boolean; linkId?: string }
          | undefined;
        if (data?.manual && data.linkId) void removeLink(data.linkId);
      }
    },
    [removeLink],
  );

  const onEdgeDoubleClick = useCallback<EdgeMouseHandler>(
    (event, edge) => {
      const data = edge.data as
        | { manual?: boolean; linkId?: string }
        | undefined;
      if (!data?.manual || !data.linkId) return;
      event.stopPropagation();
      const current = typeof edge.label === "string" ? edge.label : "";
      const next = window.prompt("Link label (empty to clear):", current);
      if (next === null) return;
      void updateLinkLabel(
        data.linkId,
        next.trim() === "" ? null : next.trim(),
      );
    },
    [updateLinkLabel],
  );

  const handleAddWorkflow = useCallback(
    async (flowId: string) => {
      await add(flowId);
    },
    [add],
  );

  const handleAddPipeline = useCallback(
    async (objectType: PipelineObjectType, pipelineId: string) => {
      await addPipeline(objectType, pipelineId);
    },
    [addPipeline],
  );

  const handleAddForm = useCallback(
    async (formId: string) => {
      await addForm(formId);
    },
    [addForm],
  );

  const fitView =
    items.length > 0 || pipelineItems.length > 0 || formItems.length > 0
      ? true
      : undefined;

  const canvasRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const wrapper = canvasRef.current;
    if (!wrapper) return;
    const REVEAL_RADIUS = 80;
    let raf = 0;
    let pending: { x: number; y: number } | null = null;
    const update = () => {
      raf = 0;
      if (!pending) return;
      const { x, y } = pending;
      pending = null;
      const cards = wrapper.querySelectorAll<HTMLElement>(".react-flow__node");
      cards.forEach((card) => {
        const r = card.getBoundingClientRect();
        const dx = Math.max(r.left - x, 0, x - r.right);
        const dy = Math.max(r.top - y, 0, y - r.bottom);
        const dist = Math.hypot(dx, dy);
        if (dist <= REVEAL_RADIUS) {
          card.dataset.near = "true";
        } else if (card.dataset.near) {
          delete card.dataset.near;
        }
      });
    };
    const onMove = (e: PointerEvent) => {
      pending = { x: e.clientX, y: e.clientY };
      if (!raf) raf = requestAnimationFrame(update);
    };
    const onLeave = () => {
      pending = null;
      const cards = wrapper.querySelectorAll<HTMLElement>(
        ".react-flow__node[data-near]",
      );
      cards.forEach((c) => delete c.dataset.near);
    };
    wrapper.addEventListener("pointermove", onMove);
    wrapper.addEventListener("pointerleave", onLeave);
    return () => {
      wrapper.removeEventListener("pointermove", onMove);
      wrapper.removeEventListener("pointerleave", onLeave);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const edgeTypes = useMemo(() => ({ manual: ManualEdge }), []);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/tools/workflows" className={styles.back}>
          ← Portals
        </Link>
        <h1 className={styles.title}>
          {portal?.nickname ?? portal?.name ?? portalId}
        </h1>
        {(loading || pipelineLoading || formLoading) && (
          <span className={styles.muted}>Loading…</span>
        )}
        {error && <span className={styles.error}>{error}</span>}
        {pipelineError && <span className={styles.error}>{pipelineError}</span>}
        {formError && <span className={styles.error}>{formError}</span>}
        {linksError && <span className={styles.error}>{linksError}</span>}
      </header>

      <div className={styles.body}>
        <CanvasSidebar active={activeTool} onToggle={setActiveTool} />

        {activeTool === "add-workflow" && portalId && (
          <AddWorkflowPanel
            portalId={portalId}
            onAdd={handleAddWorkflow}
            placedFlowIds={placedFlowIds}
            onClose={() => setActiveTool(null)}
          />
        )}

        {activeTool === "add-pipeline" && portalId && (
          <AddPipelinePanel
            portalId={portalId}
            onAdd={handleAddPipeline}
            placedKeys={placedPipelineKeys}
            onClose={() => setActiveTool(null)}
          />
        )}

        {activeTool === "add-form" && portalId && (
          <AddFormPanel
            portalId={portalId}
            onAdd={handleAddForm}
            placedFormIds={placedFormIds}
            onClose={() => setActiveTool(null)}
          />
        )}

        <div className={styles.canvas} ref={canvasRef}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodesChange={onNodesChange}
            onConnect={onConnect}
            onEdgeClick={onEdgeClick}
            onEdgeDoubleClick={onEdgeDoubleClick}
            onEdgesDelete={onEdgesDelete}
            nodesConnectable
            connectionMode={ConnectionMode.Loose}
            elementsSelectable
            fitView={fitView}
            minZoom={0.1}
            maxZoom={2}
            proOptions={{ hideAttribution: true }}
          >
            <Background />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable />
            <AlignGuides vGuide={vGuide} hGuide={hGuide} />
          </ReactFlow>
        </div>
      </div>
    </div>
  );
}
